/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * CART PROMOTION TIERS (store-specific)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Powers <cart-promo-tiers> rendered by snippets/cart-promo-tiers.liquid (cart drawer + cart page).
 * All display logic is computed in Liquid from Shopify's cart data; this script only animates the tier
 * progress, newly reached tiers and the gift panel between re-renders.
 */
(function () {
  const state = { lastProgress: null, lastTier: null, lastHeadline: null, lastGiftReached: null };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class CartPromoTiers extends HTMLElement {
    connectedCallback() {
      this._animate();
    }

    _animate() {
      const target = parseFloat(this.dataset.progress) || 0;
      const tier = parseInt(this.dataset.tier || '0', 10);
      const headline = this.dataset.headline || '';
      const giftReached = this.dataset.giftReached === 'true';
      const previousProgress = state.lastProgress;
      const previousTier = state.lastTier;
      const previousHeadline = state.lastHeadline;
      const previousGiftReached = state.lastGiftReached;

      state.lastProgress = target;
      state.lastTier = tier;
      state.lastHeadline = headline;
      state.lastGiftReached = giftReached;

      if (previousProgress === null || reducedMotion()) {
        return;
      }

      if (previousProgress !== target) {
        this.style.setProperty('--promo-progress', previousProgress);
        this.classList.add('is-animating');

        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.style.setProperty('--promo-progress', target));
        });
      }

      if (previousTier !== null && tier > previousTier) {
        const nodes = Array.from(this.querySelectorAll('.promo-tiers__node'));

        nodes.forEach((node, index) => {
          if (index + 1 > previousTier && node.classList.contains('is-reached')) {
            node.classList.remove('is-reached');
            node.classList.add('is-animating');

            requestAnimationFrame(() => {
              requestAnimationFrame(() => node.classList.add('is-reached'));
            });
          }
        });
      }

      if (previousHeadline !== headline) {
        const headlineElement = this.querySelector('.promo-tiers__headline');
        headlineElement && headlineElement.classList.add('is-changed');
      }

      if (giftReached && previousGiftReached === false) {
        const giftNote = this.querySelector('.promo-tiers__gift');
        giftNote && giftNote.classList.add('is-entering');
      }
    }
  }

  if (!window.customElements.get('cart-promo-tiers')) {
    window.customElements.define('cart-promo-tiers', CartPromoTiers);
  }
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * OPEN THE CART DRAWER AFTER ADDING FROM A QUICK VIEW (store-specific)
 * ------------------------------------------------------------------------------------------------------------
 *
 * With cart type "drawer", Focal opens the cart drawer after an add-to-cart, except when the product form lives
 * inside another drawer (so that adding from the cart's own recommendations does not re-open it). The desktop
 * quick view is such a drawer, so Focal leaves it open and shows an "item added / view cart" notice linking to the
 * cart page instead. The store wants the cart drawer everywhere and the cart page only as a fallback: close the
 * quick view and open the cart drawer once it has re-rendered.
 */
(function () {
  let openCartAfterRefresh = false;

  document.addEventListener('variant:added', (event) => {
    const quickBuy = event.target.closest && event.target.closest('quick-buy-drawer, quick-buy-popover');

    if (!quickBuy || window.themeVariables.settings.cartType !== 'drawer') {
      return;
    }

    openCartAfterRefresh = true;
    quickBuy.open = false;
  });

  document.documentElement.addEventListener('cart:refresh', (event) => {
    if (!openCartAfterRefresh || !event.detail) {
      return;
    }

    openCartAfterRefresh = false;
    event.detail.openMiniCart = true;
  }, true);
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * TAP A PRODUCT IMAGE TO OPEN IT FULL SCREEN ON PHONES (store-specific)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Focal only opens the product image zoom (the theme's PhotoSwipe modal, <product-image-zoom>) from the small
 * zoom icon over the image. On phones the store hides that icon (see the end of theme.css) and opens the same
 * modal when the shopper taps the image itself. Pinch-zoom, panning, the close button and the slide dots are
 * PhotoSwipe's own. Desktop keeps Focal's behaviour (click = previous/next slide, zoom icon = open the modal).
 *
 * The modal is opened through Focal's own zoom toggle button (hidden by CSS on phones, but still wired), so the
 * button's expanded state stays in sync; opening <product-image-zoom> directly makes the button re-open it and
 * creates a second PhotoSwipe instance that cannot be closed.
 *
 * Flickity calls preventDefault() on the click that ends a drag, so a swipe between slides never opens the modal.
 */
(function () {
  const isPhone = () => window.matchMedia(window.themeVariables.breakpoints.phone).matches;

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || !isPhone()) {
      return;
    }

    const mediaItem = event.target.closest('.product__media-item[data-media-type="image"]');

    if (!mediaItem || event.target.closest('a, button')) {
      return;
    }

    const productMedia = mediaItem.closest('product-media');
    const imageZoom = productMedia && productMedia.querySelector('product-image-zoom');

    if (!imageZoom || imageZoom.open) {
      return;
    }

    event.preventDefault();

    const zoomButton = productMedia.querySelector(`.product__zoom-button[aria-controls="${imageZoom.id}"]`);

    if (zoomButton) {
      zoomButton.click();
    } else {
      imageZoom.open = true;
    }
  });
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * TAP TO ZOOM INSIDE THE PRODUCT IMAGE MODAL ON PHONES (store-specific)
 * ------------------------------------------------------------------------------------------------------------
 *
 * In Focal's PhotoSwipe modal a single finger only swipes between photos; zooming needs a pinch or a double tap,
 * which shoppers rarely discover. PhotoSwipe fires "pswpTap" for a single tap (never for a double tap, which it
 * reports separately). Focal only acts on mouse taps; on phones this zooms in to the theme's mobile zoom factor
 * at the finger, and a second tap zooms back out, mirroring Focal's own double-tap handler and reusing the same
 * PhotoSwipe zoom transition. Dragging a zoomed image pans it (PhotoSwipe default). Pinch and double tap still work.
 */
(function () {
  const isPhone = () => window.matchMedia(window.themeVariables.breakpoints.phone).matches;

  document.addEventListener('pswpTap', (event) => {
    if (!isPhone() || !event.detail || event.detail.pointerType === 'mouse') {
      return;
    }

    const image = event.target.closest && event.target.closest('.pswp__img');
    const imageZoom = image && image.closest('product-image-zoom');
    const photoSwipe = imageZoom && imageZoom.photoSwipeInstance;

    if (!photoSwipe || !photoSwipe.currItem) {
      return;
    }

    const initialZoomLevel = photoSwipe.currItem.initialZoomLevel;
    const maxZoomLevel = window.themeVariables.settings.mobileZoomFactor || 2;
    const point = event.detail.releasePoint;

    if (photoSwipe.getZoomLevel() !== initialZoomLevel) {
      photoSwipe.zoomTo(initialZoomLevel, point, 333);
    } else {
      photoSwipe.zoomTo(initialZoomLevel < 0.7 ? 1 : maxZoomLevel, point, 333);
    }
  });
})();
